import { useQuery } from "@tanstack/react-query";

export interface UserPreferencesResponse {
  inactivityTimeoutEnabled?: boolean;
  inactivityTimeoutMinutes?: number;
  timezone?: string | null;
}

/**
 * Loads the user's preferences and derives the inactivity-logout gating from
 * them. Extracted from App.tsx's Router so the fail-safe behaviour is
 * directly testable.
 *
 * Invariants (regression: production users with the toggle OFF were logged
 * out after a transient fetch failure fell back to the 20-minute default):
 * - The timer must NEVER be armed while the preference is still loading or
 *   after a failed fetch — `inactivityEnabled` is false until real data
 *   arrives.
 * - The fetch retries and refetches on window focus so a brief connection
 *   hiccup cannot leave the app permanently without the real preference.
 */
export function useInactivityPreference(isAuthenticated: boolean) {
  const { data: userPrefs } = useQuery<UserPreferencesResponse>({
    queryKey: ["/api/user/preferences"],
    enabled: isAuthenticated,
    // The inactivity-logout timer depends on this response; if the single
    // default fetch fails the user would be logged out on the 20-min default
    // despite having the toggle off. Retry and refresh on focus so the real
    // preference always wins.
    retry: 3,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });

  // Never arm the logout timer from defaults: until the user's actual
  // preference has loaded, the timer stays off. Users who enabled it get it
  // armed seconds later once the response arrives.
  const inactivityEnabled = userPrefs ? (userPrefs.inactivityTimeoutEnabled ?? true) : false;
  const inactivityMinutes = userPrefs?.inactivityTimeoutMinutes ?? 20;

  return { userPrefs, inactivityEnabled, inactivityMinutes };
}
